import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Container from '@/components/Container';
import CategoryNav from '@/components/home/categoryNav';
import IndexNav from '@/components/home/Index/IndexNav/IndexNav';
import TastingList from '@/components/tasting/TastingList';
import TastingScopeFilter, { TastingScope } from '@/components/tasting/TastingScopeFilter';
import TastingsMeta from '@/components/seo/meta/TastingsMeta';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { PrimaryButton, PrimaryButtonLink } from '@/components/common/PrimaryButton';
import { BlogEnum } from '@/types/translations/BlogEnum';
import { SkuEnum } from '@/types/translations/SkuEnum';
import { TastingCardData } from '@/utilites/tasting-event';
import { useFetchPaginatedTastings } from '@/useQuery/queries/tastings';

/** One large card plus four rows of three. */
const PAGE_SIZE = 13;

/**
 * The `/tastings` overview.
 *
 * The magazine page with tastings in it: same hero, same card geometry, same
 * "Load More", same sign-up band at the foot. Until now the only way to reach
 * a tasting was a direct link or the "Upcoming Tastings" strip at the bottom of
 * another one.
 *
 * Cards link to `/tastings/{slug}`. There is one detail page for a tasting and
 * this page is not a second one — prettier URLs are their own ticket.
 *
 * The Upcoming/Past chips switch the query rather than filtering what is
 * loaded: the two are sorted in opposite directions, and there will eventually
 * be more past tastings than fit on one page.
 */
const Tastings = () => {
  const router = useRouter();
  const { translate } = useTypedTranslation<SkuEnum>('sku');
  const { translate: translateBlog } = useTypedTranslation<BlogEnum>('blog');

  const [scope, setScope] = useState<TastingScope>('upcoming');
  const [page, setPage] = useState(1);
  const [tastings, setTastings] = useState<TastingCardData[]>([]);
  /**
   * Kept in state next to the list rather than read straight off the query,
   * so that "is there another page" and "what is on the page" always come from
   * the same response. Read live, the count is right one render before the
   * list catches up — with a single tasting that is a "Load More" button
   * flashing under a list that is already complete.
   */
  const [pageCount, setPageCount] = useState(0);

  const tastingsQuery = useFetchPaginatedTastings(
    router.locale as string,
    scope,
    page,
    PAGE_SIZE
  );

  useEffect(() => {
    const data = tastingsQuery.data?.data;
    if (Array.isArray(data)) {
      // Appended, because "Load More" adds a page rather than replacing one.
      // Keyed by slug so a page re-fetched from cache cannot double a card.
      setTastings(prev => {
        const seen = new Set(prev.map(tasting => tasting.slug));
        return [...prev, ...data.filter(tasting => !seen.has(tasting.slug))];
      });
      setPageCount(tastingsQuery.data?.meta?.pagination?.pageCount || 0);
    }
  }, [tastingsQuery.data]);

  const handleScopeSelect = (next: TastingScope) => {
    if (next === scope) return;
    // A different query, not a filter — the list starts over.
    setScope(next);
    setPage(1);
    setTastings([]);
    setPageCount(0);
  };

  // A page that is not the last one. Not "total > loaded": that is true for a
  // moment on every first load, and on the empty Past tab it is true for as
  // long as the previous scope's response is still the current query data.
  const showLoadMore = page < pageCount;

  return (
    <>
      <TastingsMeta />
      <div className="tw-bg-white max-nine:tw-hidden">
        <div className="tw-container tw-mx-auto tw-max-w-7xl tw-px-3 tw-pt-4">
          <IndexNav />
        </div>
      </div>
      <div className="tw-mb-2 tw-mt-4 nine:tw-hidden">
        <CategoryNav userId={null} />
      </div>
      <div className="tw-w-full tw-bg-blogBg tw-bg-cover tw-bg-center">
        <div className="tw-py-40 max-nine:tw-py-20 max-sm:tw-bg-main-shades-100 max-sm:tw-bg-opacity-60">
          <Container>
            <div className="tw-w-2/5 max-sm:tw-w-full max-sm:tw-text-center">
              <div className="tw-w-fit">
                <h1 className="tw-font-prata tw-text-6xl tw-font-normal tw-text-text max-nine:tw-text-2xl">
                  Tastings
                </h1>
                <hr
                  className="tw-m-0 tw-my-4 tw-h-1 tw-w-5/6 tw-rounded-full tw-border-t-0 tw-border-none tw-bg-custom-gold tw-p-0 tw-opacity-100 max-nine:!tw-w-[100%] max-sm:tw-w-[150%]"
                  aria-hidden="true"
                />{' '}
              </div>
              <p className="tw-font-plusJakartaSans tw-text-xl tw-font-medium tw-text-text max-nine:!tw-text-base max-sm:tw-w-[80%] max-sm:tw-text-left">
                {translate(SkuEnum['tasting.overview_subtitle'])}
              </p>
            </div>
          </Container>
        </div>
      </div>

      <Container className="tw-mt-8">
        <TastingScopeFilter scope={scope} onScopeSelect={handleScopeSelect} />
      </Container>

      <TastingList
        tastings={tastings}
        loading={tastingsQuery.isLoading}
        heading={translate(
          scope === 'upcoming'
            ? SkuEnum['tasting.filter_upcoming']
            : SkuEnum['tasting.filter_past']
        )}
      />

      {showLoadMore && (
        <div className="tw-mt-8 tw-flex tw-justify-center">
          <PrimaryButton onClick={() => setPage(prev => prev + 1)}>
            {translate(SkuEnum['tasting.load_more'])}
          </PrimaryButton>
        </div>
      )}

      <div className="tw-mb-[-32px] tw-mt-24 tw-w-full tw-bg-blogSignUp tw-bg-cover tw-bg-center">
        <div className="tw-h-full tw-w-full tw-bg-main tw-bg-opacity-90">
          <Container className="tw-px-16 tw-py-20">
            <div className="tw-flex tw-items-center tw-justify-between max-sm:tw-flex-col max-sm:tw-items-center max-sm:tw-text-center">
              <div>
                <h2 className="tw-font-prata tw-text-6xl tw-font-normal tw-text-white max-nine:tw-text-xl">
                  {translateBlog(BlogEnum.you_dont_have_an_account_yet)}
                </h2>
                <p className="tw-mt-4 tw-font-plusJakartaSans tw-text-3xl tw-font-semibold tw-text-white max-nine:!tw-text-base">
                  {translateBlog(BlogEnum.create_it_right_now)}
                </p>
              </div>
              <PrimaryButtonLink
                href="/auth/sign-up"
                className="tw-px-16 tw-text-xl tw-font-semibold"
              >
                {translateBlog(BlogEnum.sign_up_now)}
              </PrimaryButtonLink>
            </div>
          </Container>
        </div>
      </div>
    </>
  );
};

export const getStaticProps = getBasicStaticSideProps([
  'common',
  'index',
  'blog',
  'sku',
  'meta',
  'index-v2',
  'header',
]);

export default Tastings;
